import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useBrandingContext } from '../contexts/BrandingContext';
import { LanguageSwitcher } from '../components/ui';

/**
 * „Забравена парола“ — честният отговор.
 *
 * 🔴 Защитата срещу изброяване на имейли (Firebase Auth) е ВКЛЮЧЕНА: за имейл
 * без акаунт Firebase отговаря „изпратено“ и не праща нищо. Тоест тази страница
 * НЕ знае дали писмо е тръгнало — затова текстът казва „ако има акаунт“, показва
 * въведения адрес (грешно изписан имейл е честа причина) и казва какво да се
 * прави, ако писмо не дойде. Измерено 16.10: 59 родители в активни семейства
 * нямат акаунт и получаваха „Email Sent!“ без писмо.
 *
 * Езикът на писмото = езикът на тази страница (`resetPassword` в useAuth).
 */

/**
 * Кодовете на Firebase Auth → ключовете на нашите съобщения (суровият текст е английски).
 * `auth/user-not-found` НЕ е тук нарочно — виж handleSubmit.
 */
const ГРЕШКИ: Record<string, string> = {
  'auth/invalid-email': 'passwordReset.invalidEmail',
  'auth/too-many-requests': 'passwordReset.tooManyRequests',
  'auth/network-request-failed': 'passwordReset.network',
};

export default function PasswordResetPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const { primaryColor } = useBrandingContext();

  const [email, setEmail] = useState('');
  // Адресът, за който писмото НАИСТИНА е поискано. Полето остава редактируемо
  // докато заявката тече — показаното след успех трябва да е изпратеното, иначе
  // поправен по време на заявката адрес прикрива точно грешката, която редът
  // „ако за <имейл> има акаунт“ трябва да покаже. [ревю 19.10]
  const [изпратенДо, setИзпратенДо] = useState('');
  const [loading, setLoading] = useState(false);
  // Ключ, не преведен низ: превключвателят е на същата страница и грешката
  // трябва да смени езика заедно с всичко останало.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Подателят на писмото е стандартният на Firebase: noreply@<проект>.firebaseapp.com.
  // ⚠️ Собствен домейн за подател в конзолата на Firebase прави този низ лъжа —
  // тогава смени и него.
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined;
  const sender = projectId ? `noreply@${projectId}.firebaseapp.com` : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const адрес = email.trim();
    setLoading(true);
    setErrorKey(null);
    setSuccess(false);
    setИзпратенДо(адрес);

    try {
      await resetPassword(адрес);
      setSuccess(true);
    } catch (error: unknown) {
      const code =
        error && typeof error === 'object' && 'code' in error ? String((error as { code: unknown }).code) : '';
      if (code === 'auth/user-not-found') {
        // Идва само ако защитата срещу изброяване бъде изключена. Същият отговор
        // като за успех: „няма акаунт“ би казал на всеки дали адресът е клиент.
        setSuccess(true);
        return;
      }
      console.error('Error sending password reset email:', error);
      setErrorKey(ГРЕШКИ[code] ?? 'passwordReset.error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <LanguageSwitcher variant="auth" activeColor={primaryColor} className="mb-6" />

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">{t('passwordReset.title')}</h1>
            <p className="mt-2 text-sm text-gray-600">{t('passwordReset.subtitle')}</p>
          </div>

          {/* Success Message */}
          {success && (
            <div
              role="status"
              className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg space-y-2"
            >
              <p className="font-medium">{t('passwordReset.successTitle')}</p>
              <p className="text-sm">{t('passwordReset.successMessage', { email: изпратенДо })}</p>
              {sender && <p className="text-sm">{t('passwordReset.successSender', { sender })}</p>}
              <p className="text-sm">{t('passwordReset.successSpam')}</p>
              <p className="text-sm">{t('passwordReset.successNoEmail')}</p>
            </div>
          )}

          {/* Error Message */}
          {errorKey && (
            <div role="alert" className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {t(errorKey)}
            </div>
          )}

          {!success ? (
            /* Reset Form */
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="email" className="label">
                  {t('passwordReset.email')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrorKey(null);
                  }}
                  className="input"
                  placeholder={t('passwordReset.emailPlaceholder')}
                />
              </div>

              {/* Submit Button */}
              <button type="submit" disabled={loading} className="w-full btn btn-primary">
                {loading ? t('passwordReset.sending') : t('passwordReset.send')}
              </button>
            </form>
          ) : (
            /* Success Actions */
            <div className="space-y-3">
              <button type="button" onClick={() => navigate('/login')} className="w-full btn btn-primary">
                {t('passwordReset.backToLogin')}
              </button>
              {/* Имейлът остава в полето: грешно изписан адрес се поправя, не се пише наново. */}
              <button type="button" onClick={() => setSuccess(false)} className="w-full btn btn-secondary">
                {t('passwordReset.resend')}
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t('passwordReset.rememberPassword')}{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                {t('passwordReset.login')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
